import React, { useEffect, useMemo, useState, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import huLocale from '@fullcalendar/core/locales/hu';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import useParam from '../../../../context/ParamContext';
import { useLoader } from '../../../../provider/LoaderProvider';
import simpletaskService from '../../../../service/simpletask.service';
import { ColorPicker } from '../../../../config/ColorPicker';
import EventChip from './EventChip';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import SimpleTaskDialog from './SimpleTaskDialog';
import useUsers from '../../../../context/UsersContext';
import useMenus from '../../../../context/MenuContext';
import SearchDialog from './SearchDialog';
import {
  AutoCompleteSelectUI,
  CheckboxUI,
  ColorDialogUI,
  ConfirmDialogUI,
  DebouncedInputUI,
  FormLabelUI,
  InputUI,
  OrderArrowsUI,
  SelectUI,
} from '../../../Interface';
import { alpha, Box, Grid, IconButton, Typography } from '@mui/material';
import masterDataService from '../../../../service/masterData.service';
import { generateAutocompleteItems, generateDefaultTime } from '../../../../utils/helper';
import useConfig from '../../../../context/ConfigContext';
import GroupDialog from './GroupDialog';
import { BatchOptionList, BatchOption } from '../../../../config/resource.planner';
import DateDialog from './DateDialog';
import fsTaskResourcePlannerService from '../../../../service/client/flamstop/fstaskResourcePlanner.service';
import ResourceMenu from './ResourceMenu';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import { colorLinkType } from '../../../../config/app';
import useSaveView from '../../../../hooks/useSaveView';
import useView from '../../../../hooks/useView';

const FSTaskResourcePlanner = () => {
  const checkboxFiltersRef = useRef(null);
  const isDraggingEventRef = useRef(false);
  const { config, getConfig } = useConfig();
  const calendarRef = useRef(null);
  const { getUser, user } = useUsers();
  const { getParam, params } = useParam();
  const saveView = useSaveView(user);
  const savedFilter = useView('savedFsPlannerFilter', user);
  const { menus, getMenus, getSubMenuNamesByUrls } = useMenus();
  const { showLoader, hideLoader } = useLoader();
  const [tasks, setTasks] = useState({ resources: [], taskEvents: [], backgroundEvents: [] });
  const [dialogOpen, setDialogOpen] = useState({ open: false, editing: false });
  const [simpleTaskDialogOpen, setSimpleTaskDialogOpen] = useState(false);
  const [task, setTask] = useState({});
  const [dateRange, setDateRange] = useState({});
  const [taskChanged, setTaskChanged] = useState(false);
  const [timeInterval, setTimeInterval] = useState({});
  const [intervalButtons, setIntervalButtons] = useState({});
  const [calendarViews, setCalendarViews] = useState({});
  const [selectedView, setSelectedView] = useState('');
  const [dialogData, setDialogData] = useState({});
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({ project: [], projectType: [] });
  const [resourceFilter, setResourceFilter] = useState({ resourceField: '' });
  const [projectFilter, setProjectFilter] = useState({ projectId: null, projectTypeId: null });
  const [defResources, setDefResources] = useState([]);
  const [copyEnabled, setCopyEnabled] = useState(false);
  const [selectedBatchOption, setSelectedBatchOption] = useState('');
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [enableSorting, setEnableSorting] = useState({
    project: false,
    employee: false,
  });
  const defResourceMenu = {
    position: null,
    data: {},
  };
  const [resourceMenu, setResourceMenu] = useState(defResourceMenu);

  const defGroupDialogData = {
    open: false,
    task: {},
  };
  const [groupDialogData, setGroupDialogData] = useState({
    defGroupDialogData,
  });
  const [prevFilter, setPrevFilter] = useState(null);
  // add: search state
  const [searchQuery, setSearchQuery] = useState('');
  // add: search dialog open state
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  // add: double-click helpers
  const clickTimerRef = useRef(null);
  const lastClickRef = useRef({ id: null, ts: 0 });
  // add: date double-click helpers
  const dateClickTimerRef = useRef(null);
  const lastDateClickRef = useRef({ key: null, ts: 0 });
  // add: header offset for fixed desktop header
  const [headerOffset, setHeaderOffset] = useState(0);
  const [copyId, setCopyId] = useState(null);

  const defConfirmDialogData = {
    open: false,
    message: '',
    handleChangeElement: () => {},
  };
  const [confirmDialogData, setConfirmDialogData] = useState(defConfirmDialogData);
  const [refetchTable, setRefetchTable] = useState(false);
  const defColorDialogData = {
    lineId: null,
    submenuUrl: null,
    type: null,
  };
  const [colorDialogData, setColorDialogData] = useState(defColorDialogData);

  const slotIntervals = {
    halfHour: { hours: 0.5 },
    hour: { hours: 1 },
    day: { days: 1 },
    week: { weeks: 1 },
    month: { months: 1 },
  };

  const calendarHourViews = {
    resourceTimelineDay: {
      slotLabelFormat: [{ hour: 'numeric', minute: '2-digit' }],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [
        { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' },
        { hour: 'numeric', minute: '2-digit' },
      ],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [
        { day: 'numeric', weekday: 'long' },
        { hour: 'numeric', minute: '2-digit' },
      ],
    },
  };

  const calendarDayViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [{ day: 'numeric', weekday: 'long' }],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [{ day: 'numeric', weekday: 'short' }],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ month: 'short' }, { day: 'numeric' }],
    },
  };

  const calendarWeekViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ week: 'numeric' }],
    },
  };

  const calendarMonthViews = {
    resourceTimelineDay: {
      slotLabelFormat: [],
    },
    resourceTimelineWeek: {
      slotLabelFormat: [],
    },
    resourceTimelineMonth: {
      slotLabelFormat: [],
    },
    resourceTimelineYear: {
      slotLabelFormat: [{ month: 'long' }],
    },
  };

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectTypeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          project: resp[0],
          projectType: resp[1],
        };

        const filteredProject = aData.project.filter((p) => p.active && !p?.projectType?.salesStage);
        const filteredProjectType = aData.projectType.filter((pt) => !pt.salesStage);

        const acOptions = {
          project: generateAutocompleteItems(filteredProject, 'projectName', 'projectId'),
          projectType: generateAutocompleteItems(filteredProjectType, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        //hideLoader();
      });
  };

  useEffect(() => {
    initDefaultValues();
    getConfig();

    const toolbarElement = document.querySelector('.fc-toolbar-chunk');
    if (toolbarElement && checkboxFiltersRef.current) {
      toolbarElement.appendChild(checkboxFiltersRef.current);
    }
  }, []);

  const refreshCalendar = () => {
    const calendarApi = calendarRef.current.getApi();
    setTimeout(() => {
      calendarApi.render();
    }, 100);
  };

  useEffect(() => {
    refreshCalendar();
  }, [tasks.taskEvents]);

  useEffect(() => {
    if (prevFilter && JSON.stringify(prevFilter) === JSON.stringify(projectFilter)) {
      return;
    }

    setPrevFilter(projectFilter);

    getResources();
  }, [projectFilter]);

  useEffect(() => {
    dayjs.extend(utc);
    dayjs.extend(timezone);
    setTimeInterval(slotIntervals.day);
    setCalendarViews(calendarDayViews);

    // @TODO promiseArray
    getParam();
    getMenus();
    if (!Object.keys(user).length) getUser();
  }, []);

  useEffect(() => {
    const indicator = document.querySelector('.fc-now-indicator');
    if (!indicator) return;

    const getScrollableParent = (el) => {
      let p = el?.parentElement;
      while (p) {
        const cs = getComputedStyle(p);
        const canScrollX = /(auto|scroll)/.test(cs.overflowX);
        if (canScrollX && p.scrollWidth > p.clientWidth) return p;
        p = p.parentElement;
      }
      return null;
    };

    const container = getScrollableParent(indicator);

    if (!container) {
      // Fallback: try built-in centering
      indicator.scrollIntoView({ block: 'nearest', inline: 'center' });
      return;
    }

    requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const indicatorRect = indicator.getBoundingClientRect();

      // indicator center position relative to container scroll origin
      const indicatorCenterX = indicatorRect.left - containerRect.left + container.scrollLeft + indicatorRect.width / 2;

      const desiredLeft = Math.max(0, indicatorCenterX - container.clientWidth / 2);
      const maxScrollLeft = Math.max(0, container.scrollWidth - container.clientWidth);

      // If centering would pass the right edge, go fully right
      container.scrollLeft = desiredLeft >= maxScrollLeft - 1 ? maxScrollLeft : desiredLeft;
    });
  }, [timeInterval, calendarViews, dateRange.startDate, dateRange.endDate]);

  // add: update header offset based on breakpoint (lg: 1024px)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const update = () => setHeaderOffset(mq.matches ? 64 : 0);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    } else {
      mq.addListener(update);
      return () => mq.removeListener(update);
    }
  }, []);

  useEffect(() => {
    setIntervalButtons(generateButtons(selectedView));
  }, [selectedView]);

  useEffect(() => {
    if (Object.keys(savedFilter).length > 0) {
      setProjectFilter((prev) => ({
        ...prev,
        projectTypeId: savedFilter?.projectTypeId ?? null,
      }));
    }
  }, [savedFilter]);

  useEffect(() => {
    if (refetchTable) {
      getTasks();
      getResources();
      setRefetchTable(false);
    }
  }, [refetchTable]);

  useEffect(() => {
    if (taskChanged) {
      getTasks();
      setTaskChanged(false);
    }
  }, [taskChanged]);

  useEffect(() => {
    if (Object.keys(user).length > 0 && dateRange.startDate && dateRange.endDate) getTasks();
  }, [dateRange.startDate, dateRange.endDate, user]);

  const getResources = () => {
    showLoader();

    fsTaskResourcePlannerService
      .getResources(projectFilter)
      .then((data) => {
        setTasks((prevState) => ({ ...prevState, resources: data }));
        setDefResources(data);
      })
      .finally(() => {
        hideLoader();
      });
  };

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      getTasks();
    }
  }, [user.userId]);

  useEffect(() => {
    if (resourceFilter.resourceField === null || resourceFilter.resourceField.trim() === '') {
      setTasks((prev) => ({
        ...prev,
        resources: defResources,
      }));
    } else {
      setTasks((prev) => ({
        ...prev,
        resources: prev.resources.filter((resource) =>
          resource.title.toLowerCase()?.includes(resourceFilter.resourceField?.trim().toLowerCase())
        ),
      }));
    }
  }, [resourceFilter]);

  const submenuNames = useMemo(() => {
    return getSubMenuNamesByUrls(['simpleTask']);
  }, [menus]);

  // add: open/close dialog based on query
  useEffect(() => {
    if (searchQuery && searchQuery.trim().length > 0) {
      setSearchDialogOpen(true);
    } else {
      setSearchDialogOpen(false);
    }
  }, [searchQuery]);

  const getTasks = () => {
    const body = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
    //showLoader();
    fsTaskResourcePlannerService
      .getTask(body)
      .then((data) => {
        const taskEvents = data.map((task) => {
          const colorStyles = getColorStyles(task?.eventData);
          return {
            ...task,
            ...colorStyles,
          };
        });

        setTasks((prevState) => ({ ...prevState, taskEvents }));
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getTaskById = (simpleTaskId, editing = false) => {
    showLoader();
    simpletaskService
      .getTaskById(simpleTaskId)
      .then((data) => {
        setTask(data);
        setDialogOpen({ open: true, editing });
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleOrderChange = (body) => {
    fsTaskResourcePlannerService.orderItemByType(body).then(() => {
      setRefetchTable(true);
    });
  };

  const resourceColumns = [
    {
      field: '',
      headerContent: '',
      headerClassNames: 'col0',
    },
    ...(enableSorting.project || enableSorting.employee
      ? [
          {
            field: 'visOrder',
            headerContent: 'Sorrend',
            headerClassNames: 'col',
            cellContent: function (arg) {
              const resource = arg.resource;
              const type = resource.extendedProps?.type;

              if (!enableSorting[type]) {
                return <Typography visibility="hidden">–</Typography>;
              }

              const id = parseInt(resource.id.split('_')[1]);

              const handleUpClick = () => {
                handleOrderChange({ type, id, direction: 'up' });
              };

              const handleDownClick = () => {
                handleOrderChange({ type, id, direction: 'down' });
              };

              return (
                <Box display="flex" alignItems="center">
                  <IconButton sx={{ m: 0, p: 0 }} onClick={handleDownClick}>
                    <ArrowDropDownCircleIcon sx={{ fontSize: '30px', color: '#30d36c' }} />
                  </IconButton>
                  <IconButton sx={{ transform: 'scaleY(-1)', m: 0, p: 0 }} onClick={handleUpClick}>
                    <ArrowDropDownCircleIcon sx={{ fontSize: '30px', color: '#30d36c' }} />
                  </IconButton>
                </Box>
              );
            },
          },
        ]
      : []),
  ];

  const getColorStyles = (event) => {
    const defaultStyles = { backgroundColor: '#fff', textColor: '#000' };

    const colorObj = ColorPicker.find((color) => color.colorName === event?.status?.color);
    return {
      backgroundColor: colorObj?.colorBgCode || defaultStyles.backgroundColor,
      textColor: colorObj?.textColor || defaultStyles.textColor,
    };
  };

  const handleDatesSet = (dateInfo) => {
    const startDate = dayjs(dateInfo.start).startOf('day');
    const endDate = dayjs(dateInfo.end).endOf('day');

    if (
      !dateRange.startDate ||
      !dateRange.endDate ||
      !startDate.isSame(dateRange.startDate) ||
      !endDate.isSame(dateRange.endDate)
    ) {
      setDateRange({ startDate, endDate });
    }

    if (selectedView !== dateInfo.view.type) setSelectedView(dateInfo.view.type);
  };

  const validateEventDrop = (eventInfo) => {
    const { event, revert, oldEvent } = eventInfo;
    const resourceObj = event.getResources()[0];
    const oldres = oldEvent.getResources()[0].id;
    const oldStart = dayjs(oldEvent.start);

    const resourceType = resourceObj.extendedProps?.type;

    const resData = resourceObj.id.split('_');
    const oldresData = oldres.split('_');

    if (oldresData[0] !== resData[0]) {
      addNotification({
        content: 'Nem helyezhető át másik erőforrás csoportra!',
        type: NotificationType.WARNING,
      });
      revert();
      return false;
    }

    // a napot ne lehessen módosítani
    if (resourceType !== 'resourceFour' && !dayjs(event.start).isSame(oldStart, 'day')) {
      addNotification({
        content: 'A nap módosítása nem engedélyezett!',
        type: NotificationType.WARNING,
      });
      revert();
      return false;
    }

    return true;
  };

  const updateEvent = (eventInfo) => {
    const { event, revert, oldEvent } = eventInfo;
    const resource = event.getResources()[0].id;
    const resData = resource.split('_');

    const oldStart = dayjs(oldEvent.start);
    const durationInMinutes = event.end ? dayjs(event.end).diff(dayjs(event.start), 'minute') : null;
    const duration = durationInMinutes / 60;
    const startDate = dayjs(event.start);
    const endDate = event.end
      ? dayjs(event.end).toISOString()
      : dayjs(event.start).add(durationInMinutes, 'minute').toISOString();

    const resourceId = parseInt(resData[1]);

    const updatedEventData = {
      resourceId,
      //startDate,
      //endDate,
      resourceField: resource.startsWith('b_') ? 'employeeId' : 'projectId',
    };

    if (!startDate.isSame(oldStart, 'day')) {
      updatedEventData.startDate = startDate.toISOString();
      updatedEventData.endDate = endDate;
    }

    //showLoader();

    const simpleTaskId = event.id;

    updatedEventData.duration = duration;
    fsTaskResourcePlannerService
      .updateTask(updatedEventData, simpleTaskId)
      .then((data) => {
        addNotification({
          content: 'Sikeres módosítás!',
          type: NotificationType.SUCCESS,
        });
        onCloseConfirmDialog();
        setTaskChanged(true);
      })
      .catch(() => {
        revert();
      });
  };

  const timeIntervalButtons = {
    halfHour: {
      text: 'Fél óra',
      click: () => {
        setTimeInterval(slotIntervals.halfHour);
        setCalendarViews(calendarHourViews);
      },
    },
    hour: {
      text: 'Óra',
      click: () => {
        setTimeInterval(slotIntervals.hour);
        setCalendarViews(calendarHourViews);
      },
    },
    day: {
      text: 'Nap',
      click: () => {
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);
      },
    },
    week: {
      text: 'Hét',
      click: () => {
        setTimeInterval(slotIntervals.week);
        setCalendarViews(calendarWeekViews);
      },
    },
    month: {
      text: 'Hónap',
      click: () => {
        setTimeInterval(slotIntervals.month);
        setCalendarViews(calendarMonthViews);
      },
    },
  };

  const generateButtons = (viewName) => {
    let retVal = {};
    switch (viewName) {
      case 'resourceTimelineDay':
        retVal = 'halfHour,hour,day';
        setTimeInterval(slotIntervals.halfHour);
        setCalendarViews(calendarHourViews);

        break;
      case 'resourceTimelineWeek':
        retVal = 'halfHour,hour,day,week';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
      case 'resourceTimelineMonth':
        retVal = 'hour,day,week,month';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
      case 'resourceTimelineYear':
        retVal = 'day,week,month';
        setTimeInterval(slotIntervals.day);
        setCalendarViews(calendarDayViews);

        break;
    }
    return retVal;
  };

  const handleDateClick = async (event) => {
    const { resource, date } = event;

    const task = copyId ? await simpletaskService.getTaskById(copyId) : {};

    if (copyId) {
      setCopyId(null);
    }

    const startDate = generateDefaultTime(date, config.defTaskFromTime);

    const duration = config?.defTaskDuration ?? 1;

    const resourceData = resource.id.split('_');

    if (resourceData[0] === 'b') {
      addNotification({
        content: 'Nem hozható létre feladat ezen az erőforrás csoporton!',
        type: NotificationType.WARNING,
      });
      return;
    }

    const { resources, ...taskWithoutResources } = task;

    let dialogData = {
      ...taskWithoutResources,
      startDate,
      endDate: startDate.add(duration, 'hour'),
      duration,
      projectId: parseInt(resourceData[1]),
    };

    setSimpleTaskDialogOpen(true);
    setDialogData(dialogData);
  };

  const handleDeleteTasks = () => {
    fsTaskResourcePlannerService.deleteTasks({ ids: selectedTaskIds }).then(() => {
      setTaskChanged(true);
      setSelectedBatchOption('');
      setSelectedTaskIds([]);
      setConfirmDialogData(defConfirmDialogData);
    });
  };

  const onClearAfterOperation = () => {
    setTaskChanged(true);
    onCloseConfirmDialog();
    setDateDialogOpen(false);
    setSelectedBatchOption('');
    setSelectedTaskIds([]);
  };

  const handleCopyTasks = (body) => {
    fsTaskResourcePlannerService.copyTasks(body).then(() => {
      onClearAfterOperation();
    });
  };

  const handleMoveTasks = (body) => {
    fsTaskResourcePlannerService.moveTasks(body).then(() => {
      onClearAfterOperation();
    });
  };

  const onCloseConfirmDialog = (withRevert = false) => {
    if (withRevert && confirmDialogData.event?.revert) {
      confirmDialogData.event.revert();
    }
    setConfirmDialogData(defConfirmDialogData);
  };

  const handleSelectBatchOption = (option) => {
    setSelectedBatchOption(option);

    switch (option) {
      case BatchOption.DELETE:
        setConfirmDialogData({
          open: true,
          message: 'Megerősíti a törlési műveletet? Az erőforrásigények is törlése kerülnek!',
          handleChangeElement: () => handleDeleteTasks(),
        });
        break;
      case BatchOption.COPY:
        setDateDialogOpen(true);
        break;
      case BatchOption.MOVE:
        setDateDialogOpen(true);
        break;
    }
  };

  const handleSaveDate = (selectedDate) => {
    const isCopy = selectedBatchOption === BatchOption.COPY;
    const isMove = selectedBatchOption === BatchOption.MOVE;

    const startDate = generateDefaultTime(selectedDate, config.defTaskFromTime);

    const duration = config?.defTaskDuration ?? 1;
    const endDate = startDate.add(duration, 'hour');

    const message = isCopy ? 'Megerősíti a másolási műveletet?' : 'Megerősíti az áthelyezési műveletet?';

    const body = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      simpleTaskIds: selectedTaskIds,
    };

    setConfirmDialogData({
      open: true,
      message,
      handleChangeElement: () => {
        if (isCopy) handleCopyTasks(body);
        if (isMove) handleMoveTasks(body);
      },
    });
  };

  const handleChangeSortEnability = (field) => {
    setEnableSorting((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleOpenColorDialog = () => {
    setColorDialogData({
      open: true,
      data: {
        id: resourceMenu.data.id,
        lineId: parseInt(resourceMenu.data.id.split('_')[1]),
        subMenuUrl: resourceMenu.data.type === 'project' ? 'Project' : resourceMenu.data.type,
        type: colorLinkType.FLAMSTOP_TASKRESOURCEPLANNER,
      },
    });
    setResourceMenu(defResourceMenu);
  };

  const handleRefetchColorChange = (id, color) => {
    setTasks((prev) => {
      const newResources = prev.resources.map((resource) => (resource.id === id ? { ...resource, color } : resource));

      const cell = document.querySelector(`[data-resource-id="${id}"] .fc-datagrid-cell-frame`);
      if (cell) {
        cell.style.backgroundColor = color ? alpha(color, 0.4) : null;
      }

      return { ...prev, resources: newResources };
    });
  };

  return (
    <div
      className="taskListCalendar taskPlanner plannerDarkerBorder dvPlannerChip"
      style={{ height: `calc(100dvh - ${headerOffset}px)` }}
    >
      <div ref={checkboxFiltersRef}>
        <Grid container spacing={0.5} sx={{ width: '100%', alignItems: 'center' }}>
          <Grid item sx={{ flex: '1 1 180px', minWidth: 180 }}>
            <InputUI
              type="search"
              label={<FormLabelUI text="Keresés" />}
              variant="standard"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid item sx={{ flex: '1 1 180px', minWidth: 180 }}>
            <AutoCompleteSelectUI
              key="project"
              name="project"
              label={<FormLabelUI text="Projekt" />}
              onChange={(_e, newVal) => {
                setProjectFilter((prev) => ({
                  ...prev,
                  projectId: newVal?.value ?? null,
                }));
              }}
              dataset={autoCompleteOptions.project}
              selectedValue={projectFilter.projectId}
              selectedLabelValue={
                autoCompleteOptions.project.find((item) => item.value === projectFilter.projectId)?.label
              }
            />
          </Grid>
          <Grid item sx={{ flex: '1 1 180px', minWidth: 180 }}>
            <AutoCompleteSelectUI
              key="projectType"
              name="projectType"
              label={<FormLabelUI text="Projekt típus" />}
              onChange={(_e, newVal) => {
                setProjectFilter((prev) => ({
                  ...prev,
                  projectTypeId: newVal?.value ?? null,
                }));
                saveView('savedFsPlannerFilter', { projectTypeId: newVal?.value ?? null });
              }}
              dataset={autoCompleteOptions.projectType}
              selectedValue={projectFilter.projectTypeId}
              selectedLabelValue={
                autoCompleteOptions.projectType.find((item) => item.value === projectFilter.projectTypeId)?.label
              }
            />
          </Grid>
          <Grid item sx={{ flex: '1 1 180px', minWidth: 180 }}>
            <DebouncedInputUI
              label={<FormLabelUI text="Erőforrás" />}
              debounceMs={100}
              setQuickFilterSearchValue={(newValue) => {
                setResourceFilter((prevState) => {
                  if (prevState.resourceField === newValue) {
                    return prevState;
                  }
                  return {
                    ...prevState,
                    resourceField: newValue,
                  };
                });
              }}
              quickFilterSearchValue={resourceFilter.resourceField}
            />
          </Grid>
          <Grid item sx={{ flex: '1 1 100px', minWidth: 100 }}>
            <Box mt={0.5}>
              <SelectUI
                label={<FormLabelUI text="Műveletek" />}
                option={BatchOptionList}
                onChange={(e) => handleSelectBatchOption(e.target.value)}
                value={selectedBatchOption}
                onClear={() => {
                  setSelectedBatchOption('');
                }}
                disabled={!selectedTaskIds.length}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box mt={1}>
              <CheckboxUI label="Másolás" onChange={() => setCopyEnabled(!copyEnabled)} checked={!!copyEnabled} />
            </Box>
          </Grid>
        </Grid>
      </div>
      <FullCalendar
        ref={calendarRef}
        height="100%"
        contentHeight="100%"
        resourceAreaColumns={resourceColumns}
        customButtons={timeIntervalButtons}
        plugins={[resourceTimelinePlugin, interactionPlugin]}
        initialView="resourceTimelineWeek"
        resources={tasks.resources}
        resourceOrder="visOrder"
        locales={[huLocale]}
        locale="hu"
        slotLabelFormat={[
          { month: 'long', year: 'numeric', weekday: 'long' },
          { hour: 'numeric', minute: '2-digit' },
        ]}
        datesSet={handleDatesSet}
        events={[...tasks.taskEvents, ...tasks.backgroundEvents]}
        // disable drag/resize while long-press is active
        editable={true}
        eventStartEditable={true}
        eventDurationEditable={false}
        dateClick={(info) => {
          if (copyId) {
            if (dateClickTimerRef.current) {
              clearTimeout(dateClickTimerRef.current);
              dateClickTimerRef.current = null;
            }
            lastDateClickRef.current = { key: null, ts: 0 };
            handleDateClick(info);
            return;
          }
          // otherwise require double-click
          const now = Date.now();
          const key = `${info.resource?.id ?? ''}-${+info.date}`;
          const last = lastDateClickRef.current;
          if (last.key === key && now - last.ts < 300) {
            if (dateClickTimerRef.current) {
              clearTimeout(dateClickTimerRef.current);
              dateClickTimerRef.current = null;
            }
            lastDateClickRef.current = { key: null, ts: 0 };
            handleDateClick(info);
          } else {
            lastDateClickRef.current = { key, ts: now };
            if (dateClickTimerRef.current) clearTimeout(dateClickTimerRef.current);
            dateClickTimerRef.current = setTimeout(() => {
              lastDateClickRef.current = { key: null, ts: 0 };
            }, 300);
          }
        }}
        nowIndicator={true}
        //eventResize={updateEvent}
        eventDrop={(eventInfo) => {
          setCopyId(null);
          const isValid = validateEventDrop(eventInfo);
          if (!isValid) return;

          const { event, oldEvent } = eventInfo;

          const oldStart = dayjs(oldEvent.start);
          const newStart = dayjs(event.start);

          const dateChanged = !newStart.isSame(oldStart, 'day');

          const message = dateChanged
            ? 'Megerősíti a műveletet? Az erőforrásigények törlése kerülnek!'
            : 'Megerősíti a műveletet?';

          setConfirmDialogData({
            open: true,
            handleChangeElement: () => updateEvent(eventInfo),
            message,
          });
        }}
        eventDragStart={() => {
          isDraggingEventRef.current = true;
        }}
        eventDragStop={() => {
          isDraggingEventRef.current = false;
        }}
        resourceLabelDidMount={(info) => {
          const res = info.resource;

          const frame = info.el.querySelector('.fc-datagrid-cell-frame');
          const color = res?.extendedProps?.color;

          frame.style.backgroundColor = color ? alpha(color, 0.4) : null;

          info.el.addEventListener('contextmenu', (e) => {
            e.preventDefault();
            setResourceMenu({
              position: { mouseX: e.clientX + 2, mouseY: e.clientY - 6 },
              data: { type: res.extendedProps.type, id: res.id },
            });
          });
        }}
        eventClick={(clickInfo) => {
          // handle only double-clicks, ignore background events
          if (clickInfo.event.display === 'background') return;

          const event = clickInfo.event;
          const id = parseInt(event.id);
          const now = Date.now();

          const last = lastClickRef.current;
          const isSameEvent = last.id === event.id;

          const isDouble = isSameEvent && now - last.ts < 300;

          if (isDouble) {
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current);
              clickTimerRef.current = null;
            }
            lastClickRef.current = { id: null, ts: 0 };

            if (params.TASKPLANNER_DOUBLE_CLICK_TASK_ENABLE === 'Y') {
              getTaskById(id, true);
            } else {
              getTaskById(id);
            }
            return;
          }

          lastClickRef.current = { id: event.id, ts: now };

          if (copyEnabled && !event.extendedProps.eventData.employeeId) {
            if (clickTimerRef.current) clearTimeout(clickTimerRef.current);

            clickTimerRef.current = setTimeout(() => {
              setGroupDialogData({
                open: true,
                task: event.extendedProps.eventData,
              });
              lastClickRef.current = { id: null, ts: 0 };
            }, 300);

            return;
          }

          if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
          clickTimerRef.current = setTimeout(() => {
            lastClickRef.current = { id: null, ts: 0 };
          }, 300);
        }}
        eventContent={(eventInfo) => {
          if (eventInfo.event.display !== 'background') {
            return (
              <EventChip
                eventInfo={eventInfo}
                setCopyId={setCopyId}
                isDraggingRef={isDraggingEventRef}
                selectedTaskState={{ selectedTaskIds, setSelectedTaskIds }}
              />
            );
          }
        }}
        eventClassNames={(arg) => {
          if (arg.event.display === 'background') return [];
          return selectedTaskIds.includes(Number(arg.event.id)) ? ['fc-task-selected'] : [];
        }}
        headerToolbar={{
          left: `prev,next,today ${intervalButtons}`,
          center: 'title',
          right: 'resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth,resourceTimelineYear',
        }}
        views={calendarViews}
        slotDuration={timeInterval}
        resourceAreaWidth="15%"
        resourceAreaHeaderContent="Erőforrás"
        scrollTimeReset={false}
        resourceLabelClassNames={(data) => {
          if (!data.resource._resource.parentId) {
            return ['fc-res-group'];
          }
        }}
        slotMinWidth={180}
        nowIndicatorClassNames="fc-now-indicator"
      />

      <SearchDialog
        open={searchDialogOpen}
        searchQuery={searchQuery}
        handleClose={() => setSearchDialogOpen(false)}
        getTaskById={getTaskById}
        onSearchChange={setSearchQuery}
      />

      <SimpleTaskDialog
        open={dialogOpen.open}
        changed={{ taskChanged, setTaskChanged }}
        task={task}
        editing={dialogOpen.editing}
        submenuNames={submenuNames}
        handleClose={() => {
          setDialogOpen({ open: false, editing: false });
        }}
      />

      <SimpleTaskDialog
        changed={{ taskChanged, setTaskChanged }}
        open={simpleTaskDialogOpen}
        submenuNames={submenuNames}
        dialogData={dialogData}
        handleClose={() => {
          setSimpleTaskDialogOpen(false);
        }}
      />

      <ConfirmDialogUI
        handleChangeElement={confirmDialogData.handleChangeElement}
        show={confirmDialogData.open}
        onHide={() => onCloseConfirmDialog(true)}
        message={confirmDialogData.message}
      />

      <ResourceMenu
        resourceMenu={resourceMenu}
        handleClose={() => setResourceMenu(defResourceMenu)}
        handleChangeSortEnability={handleChangeSortEnability}
        handleOpenColorDialog={handleOpenColorDialog}
      />

      <ColorDialogUI
        open={colorDialogData.open}
        handleClose={() => setColorDialogData(defColorDialogData)}
        data={colorDialogData.data}
        refetchData={handleRefetchColorChange}
      />

      <GroupDialog
        groupDialogData={groupDialogData}
        handleClose={() => setGroupDialogData(defGroupDialogData)}
        groups={autoCompleteOptions.project}
        setTaskChanged={setTaskChanged}
      />

      <DateDialog open={dateDialogOpen} handleClose={() => setDateDialogOpen(false)} onSave={handleSaveDate} />
    </div>
  );
};

export default FSTaskResourcePlanner;
